import { coveragesValidations } from "../../../RFP/coverages/CoverageForms/coveragesValidations";
import { Coverage } from "../../../../types/rfpApi";
import { initDetailsValues } from "../../../RFP/coverages/CoverageForms/coverages";

import {
  CoverageDetails,
  SelectedCoverageDetails,
  ProductsResponse,
  NextStepDetails,
  ProductElement,
  Details,
} from "./types";
import { DENTAL } from "./constants";

function getAllSelectedCoverages(
  selectedCoverages: SelectedCoverageDetails,
): CoverageDetails[] {
  return Object.entries(selectedCoverages).flatMap(([_, val]) => val);
}

function getCurrentCoverageIndex(
  coverages: CoverageDetails[],
  currentCoverageId: number | undefined,
): number {
  return coverages.findIndex((c) => c.id === currentCoverageId);
}

function areAllCoveragesValid(coverages: CoverageDetails[]): boolean {
  return coverages.every((c) => c.isValid);
}

function isCoverageInvalid(coverage: CoverageDetails): boolean {
  return coverage.details.plans.some((p) => !isPlanValid(coverage, p));
}

function isPlanValid(
  coverage: CoverageDetails,
  plan: CoverageDetails["details"]["plans"][number],
): boolean {
  const errors = Object.values(
    coveragesValidations[coverage.name]({
      ...plan,
      isCurrentCoverage: coverage.isCurrentCoverage,
    }),
  );
  return !errors.some((v) => Boolean(v)); // Return true when NO errors (plan is valid)
}

function productsResponseSelect() {
  return (data: ProductsResponse): ProductsResponse => {
    const products = data.products.filter((item) => item.name !== DENTAL);
    const currentClientProducts = data.clientProducts || [];
    return products.reduce(
      (acc, c) => {
        const isCurrentCoverage = currentClientProducts.some(
          (cc) => cc.id === c.id,
        );
        if (isCurrentCoverage) {
          acc.clientProducts.push({
            ...c,
            isCurrentCoverage,
          });
        } else {
          acc.products.push({
            ...c,
            isCurrentCoverage,
          });
        }
        return acc;
      },
      { clientProducts: [], products: [] } as ProductsResponse,
    );
  };
}

function findNextStepDetails(
  coverages: CoverageDetails[],
  currentCoverageId: number,
): NextStepDetails | null {
  const coverage = coverages.find(
    (c) => !c.isValid && c.id !== currentCoverageId,
  );
  return coverage ? createNextStepDetails(coverage) : null;
}

function createNextStepDetails(
  coverageDetails: CoverageDetails,
  planIndex?: number,
): NextStepDetails {
  return {
    name: coverageDetails.name,
    id: coverageDetails.id,
    planIndex,
  };
}

function getNextStepDetailsForMultiPlans(
  coverage: CoverageDetails,
  currentPlanKey: number,
): NextStepDetails | null {
  const plans = coverage.details.plans;
  if (plans.length === 1) {
    return null;
  }

  const currentPlan = plans[currentPlanKey];
  const planIndex = plans.findIndex(
    (p) => p.name !== currentPlan.name && !isPlanValid(coverage, p),
  );
  if (planIndex > -1) {
    return {
      name: plans[planIndex].name,
      id: coverage.id,
      planIndex,
    };
  }
  return null;
}

function updateSelectedCoveragesWithCoverage(
  selectedCoverages: SelectedCoverageDetails,
  coverage: CoverageDetails,
): SelectedCoverageDetails {
  return Object.entries(selectedCoverages).reduce<SelectedCoverageDetails>(
    (acc, [key, coverages]) => ({
      ...acc,
      [key]: coverages.map((cov) => (cov.id === coverage.id ? coverage : cov)),
    }),
    {
      clientProducts: [],
      products: [],
    },
  );
}

function parseProduct({
  product,
  isCurrentCoverage = false,
  currentProducts = [],
}: {
  product: ProductElement;
  isCurrentCoverage?: boolean;
  currentProducts?: Coverage[];
}): CoverageDetails {
  const currentProduct = currentProducts.find((cp) => cp.name === product.name);
  const incomingDetails: Details = {
    ...(initDetailsValues(product.name) as Details),
    ...currentProduct?.details,
  };

  return {
    ...product,
    details: incomingDetails,
    isCurrentCoverage,
    isValid: true,
  };
}

export {
  getAllSelectedCoverages,
  getCurrentCoverageIndex,
  areAllCoveragesValid,
  isCoverageInvalid,
  productsResponseSelect,
  findNextStepDetails,
  createNextStepDetails,
  getNextStepDetailsForMultiPlans,
  updateSelectedCoveragesWithCoverage,
  parseProduct,
};
